import { createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import type { AxiosError } from "axios";
import axios from "axios";
import _ from "lodash";
import isHotkey from "is-hotkey";
import type { SyntheticEvent } from "react";
import download from "slate-transcript-editor/util/downlaod/index";
import exportAdapter, { isCaptionType } from "slate-transcript-editor/util/export-adapters";
import convertSlateToDpe from "slate-transcript-editor/util/export-adapters/slate-to-dpe";
import insertTimecodesInLineInSlateJs from "slate-transcript-editor/util/insert-timecodes-in-line-in-words-list";
import type { Story } from "@theirstory/common/api_types/backend";

import { notifyError, notifySuccess } from "../../actions/notify";
import {
  buildIndexes,
  getHighlightedChapters,
  getHighlightedParagraphs,
  transformIndexFormat,
} from "../../../Pages/Hub/transcript/utils";
import type { IndexInterface } from "../../../Pages/Hub/transcript/interfaces";
import { checkDpeTranscriptDoc, convertDpeToSlate, sanitizeSlateForDpe } from "../../../components/utils/conversions";
import { fixParagraph } from "../../../components/utils/story_authoring_align";
import type { DpeDocument, SlateDocument } from "../../../components/utils/types";
import type { RootState } from "../rootReducer";
import type { ActionTypeHelper, AppDispatch } from "../rootStore";
import { betterUnwrapResult } from "../utils";
import { sendPushNotification } from "../../../components/utils/notifications";

import { sortIndexesNewestFirst } from "./utils";
import type { Clip, StoryIndex } from "./reducer";
import {
  AddStoryIndex,
  DeleteStoryIndex,
  PlaybackProgress,
  SetHasUnsavedChanges,
  SetLastSaved,
  SetSkipTime,
  StoryLoaded,
  StoryPlaybackStateRefreshed,
  TogglePlaying,
  TranscriptVersionsLoaded,
  TranscriptVersionPreviewLoaded,
  TranscriptVersionPreviewLoading,
  SetHubPageParagraphs,
  SetParagraphSearchValue,
  SetTagSearchValue,
  SetNewWordIndex,
  SetChaptersSearchValue,
  SetShowIndexes,
  SetIndexId,
  UpdateStoryClips,
  UpdateClipModal,
  SetIsLoadingClips,
  ClearStoryAuthoringState,
  SetAutoScroll,
  SetHighlightWords,
  SetHasHighlightedStartToEndWords,
  SetShowOnlySummaries,
  TranscriptUpdated,
  SetTranscriptLoadingState,
  SetIsSearchActive,
  SetStoryDuration,
} from "./reducer";

export const skipIncrement = 10;

export const ffw: ActionTypeHelper = (dispatch, getState) => {
  const { progressSeconds } = getState().storyAuthoring;
  dispatch(SetSkipTime(progressSeconds + skipIncrement));
};
export const rwd: ActionTypeHelper = (dispatch, getState) => {
  const { progressSeconds } = getState().storyAuthoring;
  dispatch(SetSkipTime(progressSeconds - skipIncrement));
};
export const playPause: ActionTypeHelper = (dispatch, getState) => {
  const { playing } = getState().storyAuthoring;
  dispatch(TogglePlaying(!playing));
};

const hotkeyMap = {
  esc: playPause,
  "ctrl+[": rwd,
  "ctrl+]": ffw,
};

export const handleHotkey =
  (event: SyntheticEvent<Element, KeyboardEvent>): ActionTypeHelper =>
  (dispatch) => {
    const foundHandler = Object.entries(hotkeyMap).find(([k]) => event && isHotkey(k, event.nativeEvent));
    if (foundHandler) {
      const [shortcut, handler] = foundHandler;
      console.log(`Handling shortcut: ${shortcut}`);
      dispatch(handler);
      event.nativeEvent?.stopPropagation();
      event.nativeEvent?.stopImmediatePropagation();
    }
  };

export type TranscriptResponse = {
  transcript: any; // IEditableTranscript;
  story: any; // IStory;
  videoURL: string;
  updated_at: string;
};

type DailyPreviewState = Pick<Story, "previewURL" | "isDailyPreview">;

const fetchDailyPreviewState = async (story: Story): Promise<DailyPreviewState> => {
  if (!story.isDailyPreviewPending) {
    return { previewURL: undefined, isDailyPreview: false };
  }

  try {
    const response = await axios.get<{ previewURL?: string }>(`/stories/${story._id}/daily-preview`);
    return response.data.previewURL ? { previewURL: response.data.previewURL, isDailyPreview: true } : {};
  } catch {
    // Keep an already-visible preview while a transient Daily request fails.
    return {};
  }
};

export const loadStory = createAsyncThunk("story/load", async (storyId: string, { dispatch }) => {
  const story = await axios.get<Story>(`/stories/${storyId}`);
  const dailyPreviewStatePromise = story.data.isDailyPreviewPending ? fetchDailyPreviewState(story.data) : null;

  let indexes;
  let transcript;
  if (story.data.transcript === "completed") {
    const transcriptProm = async () => {
      try {
        return await axios.get<TranscriptResponse>(`/transcripts/${storyId}`);
      } catch (e) {
        const err = <AxiosError>e;
        if (err.response.status === 400) {
          return null;
        } else {
          throw err;
        }
      }
    };

    [indexes, transcript] = await Promise.all([
      axios.get<Array<StoryIndex>>(`/stories/${storyId}/index`),
      transcriptProm(),
    ]);
  }

  const transcriptUpdatedAt = transcript?.data?.transcript?.updated_at;

  if (transcript && !checkDpeTranscriptDoc(transcript?.data?.transcript)) {
    Sentry.captureMessage("Loading transcript with validation failures");
  }

  dispatch(
    StoryLoaded({
      story: story.data,
      transcriptSlate: transcript ? convertDpeToSlate(transcript?.data?.transcript) : null,
      transcriptUpdatedAt: transcriptUpdatedAt && new Date(transcriptUpdatedAt),
      indexes: indexes ? indexes.data : [],
    })
  );

  if (dailyPreviewStatePromise !== null) {
    void dailyPreviewStatePromise.then((dailyPreviewState) => {
      dispatch(
        StoryPlaybackStateRefreshed({
          _id: story.data._id,
          ...dailyPreviewState,
        })
      );
    });
  }
});

export const loadVersions = createAsyncThunk("transcript_versions/load", async (storyId: string, { dispatch }) => {
  try {
    const { data } = await axios.get(`/transcripts/${storyId}/versions`);
    const sortedVersions = data?.reverse() || [];

    dispatch(
      TranscriptVersionsLoaded({
        versions: sortedVersions,
      })
    );
  } catch (error) {
    console.log({ error });
  }
});

export const loadVersionPreview = createAsyncThunk(
  "transcript_version/preview",
  async ({ storyId, filename }: { storyId: string; filename: string }, { dispatch }) => {
    dispatch(
      TranscriptVersionPreviewLoading({
        isLoading: true,
      })
    );
    try {
      const { data } = await axios.get(`/transcripts/${storyId}/version/file/${filename}`);
      const previewSlate = convertDpeToSlate(data?.transcript);

      dispatch(
        TranscriptVersionPreviewLoaded({
          preview: previewSlate || [],
        })
      );
    } catch (error) {
      dispatch(
        TranscriptVersionPreviewLoading({
          isLoading: false,
        })
      );
      console.log({ error });
    }
  }
);

export const createIndex = createAsyncThunk(
  "index/create",
  async ({ storyId, contents }: { storyId: string; contents? }, { dispatch }) => {
    const newIdx = await axios.put<{ _id: string } & StoryIndex>(`/stories/${storyId}/index`);

    const returnedIndex = newIdx.data;

    if (contents) {
      const serverProps = _.pick(returnedIndex, ["_id", "created_at", "updated_at"]);
      dispatch(AddStoryIndex({ ...contents, ...serverProps }));
    } else {
      dispatch(AddStoryIndex(returnedIndex as unknown as StoryIndex));
    }

    return returnedIndex;
  }
);

export const deleteIndex = createAsyncThunk(
  "index/delete",
  async ({ story_id: storyId, index_id: indexId }: { story_id: string; index_id: string }, { dispatch }) => {
    dispatch(DeleteStoryIndex({ index_id: indexId }));

    const response = await axios.delete(`/stories/${storyId}/index/${indexId}`);

    if (response.status !== 204) {
      throw new Error(`Error deleting index ${indexId}: ${response.statusText} ${response.data}`);
    }
  }
);

export const createClip = createAsyncThunk(
  "clip/create",
  async (
    {
      storyId,
      title,
      description,
      transcript,
      clipStart,
      clipEnd,
      tags,
    }: {
      storyId: string;
      title: string;
      description: string;
      transcript: string;
      clipStart: string;
      clipEnd: string;
      tags: string;
    },
    { dispatch }
  ) => {
    // Resolves with the created clip (callers like the theme page attach it to a theme).
    try {
      const { data } = await axios.post<{ _id: string; duration?: number; thumbnailURL?: string }>(
        `/stories/${storyId}/create_clip`,
        {
          title: title,
          description: description,
          transcript: transcript,
          start: clipStart,
          end: clipEnd,
          tags: tags,
        }
      );
      dispatch(getClips({ story_id: storyId }));
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const deleteMultipleClips = createAsyncThunk(
  "clips/get",
  async ({ story_id: storyId, clipsToDelete }: { story_id: string; clipsToDelete: Clip[] }, { dispatch }) => {
    try {
      const { data } = await axios.delete(`/stories/${storyId}/delete_multiple_clip`, {
        data: clipsToDelete.map((clip) => ({ id: clip._id })),
      });
      if (data) {
        dispatch(getClips({ story_id: storyId }));
      }
      dispatch(SetIsLoadingClips({ isLoading: false }));
    } catch (error) {
      console.log(error);
      dispatch(notifyError(`Failed to delete selected clips. Please try again.`));
    }
  }
);

export const getClips = createAsyncThunk(
  "clips/get",
  async ({ story_id: storyId }: { story_id: string }, { dispatch }) => {
    try {
      const { data } = await axios.get(`/stories/${storyId}/list_clip`);
      const formatClips = data.map((clip) => ({
        ...clip,
        thumbnailURL: clip.getThumbnailURL ? clip.getThumbnailURL : "",
      }));
      dispatch(UpdateStoryClips({ clips: formatClips }));
      dispatch(SetIsLoadingClips({ isLoading: false }));
    } catch (error) {
      console.log(error);
    }
  }
);

export const deleteClip = createAsyncThunk(
  "clip/delete",
  async ({ story_id: storyId, clipId }: { story_id: string; clipId: string }, { dispatch }) => {
    try {
      const { data } = await axios.delete(`/stories/${storyId}/delete_clip/${clipId}`);

      if (data) {
        dispatch(getClips({ story_id: storyId }));
      }
    } catch (error) {
      console.log(error);
      dispatch(notifyError(`Failed to delete clip. Please try again.`));
    }
  }
);

export const getAudioDownload = createAsyncThunk(
  "clip/generateAudioDownload",
  async ({ story_id: storyId, clipId }: { story_id: string; clipId: string }, { dispatch }) => {
    try {
      const { data } = await axios.post(`/stories/${storyId}/${clipId}/generate_audio_download`);

      if (data) {
        dispatch(getClips({ story_id: storyId }));
      }
    } catch (error) {
      console.log(error);
    }
  }
);

export const updateModalCreateClip =
  ({
    clipDesc,
    clipTranscript,
    clipUrl,
    clipStart,
    clipEnd,
    clipDuration,
    clipTitle,
    clipKeywords,
  }: {
    clipDesc?: string;
    clipTranscript?: string;
    clipUrl: string;
    clipStart: number | string;
    clipEnd: number | string;
    clipDuration: number;
    clipTitle?: string;
    clipKeywords?: string;
  }) =>
  (dispatch) => {
    const clip = {
      videoUrl: clipUrl,
      clipFragmentUrl: "",
      clipExtendedFragmentUrl: "",
      description: clipDesc || "",
      transcript: clipTranscript || "",
      start: clipStart,
      end: clipEnd,
      tags: clipKeywords,
      title: clipTitle || "",
      storyDuration: clipDuration,
      isOpen: true,
      isAudioFile: false,
    };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalEditClip =
  ({
    clipId,
    clipDesc,
    clipTranscript,
    clipTitle,
    clipKeywords,
    clipStart,
    clipEnd,
    clipDuration,
    clipFragmentUrl,
    clipExtendedFragmentUrl,
    isAudioFile,
    videoUrl,
  }) =>
  (dispatch) => {
    // Legacy clips have no `transcript`; their `description` actually holds the
    // transcript. Surface it in the Transcript field and clear Description so an
    // edit self-heals the record onto the new model.
    const isLegacy = !clipTranscript;
    const clip = {
      _id: clipId,
      clipFragmentUrl: clipFragmentUrl,
      clipExtendedFragmentUrl: clipExtendedFragmentUrl,
      description: isLegacy ? "" : clipDesc,
      transcript: isLegacy ? clipDesc || "" : clipTranscript,
      start: clipStart,
      end: clipEnd,
      tags: clipKeywords,
      title: clipTitle,
      storyDuration: clipDuration || 0,
      isAudioFile: isAudioFile,
      videoUrl: videoUrl,
      isOpen: true,
    };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalTags =
  ({ clipKeywords }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, tags: clipKeywords };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalDesc =
  ({ clipDesc }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, description: clipDesc };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalTranscript =
  ({ clipTranscript }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, transcript: clipTranscript };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalTitle =
  ({ clipTitle }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, title: clipTitle };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalStart =
  ({ clipStart }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, start: clipStart };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateModalEnd =
  ({ clipEnd }) =>
  (dispatch, getState: () => RootState) => {
    const clipModal = getState().storyAuthoring.clipModal;

    const clip = { ...clipModal, end: clipEnd };

    dispatch(UpdateClipModal({ clip }));
  };

export const updateCloseModal = () => (dispatch, getState: () => RootState) => {
  const clipModal = getState().storyAuthoring.clipModal;

  const clip = { ...clipModal, isOpen: false };

  dispatch(UpdateClipModal({ clip }));
};

export const updateClip = createAsyncThunk(
  "clip/update",
  async (
    {
      storyId,
      clipId,
      clipName,
      clipDesc,
      clipTranscript,
      clipStart,
      clipEnd,
      clipKeywords,
    }: {
      storyId: string;
      clipId: string;
      clipName: string;
      clipDesc: string;
      clipTranscript: string;
      clipStart: string;
      clipEnd: string;
      clipKeywords: string;
    },
    { dispatch }
  ) => {
    try {
      const { data } = await axios.put(`/stories/${storyId}/edit_clip/${clipId}`, {
        title: clipName,
        description: clipDesc,
        transcript: clipTranscript,
        start: clipStart,
        end: clipEnd,
        tags: clipKeywords,
      });

      if (data) {
        dispatch(getClips({ story_id: storyId }));
      }
    } catch (error) {
      console.log(error);
    }
  }
);

export const getClip = createAsyncThunk<
  void,
  { storyId: string; clipId: string; attempt?: number },
  { state: RootState }
>("clip/get", async ({ storyId, clipId, attempt = 0 }, { getState, dispatch }) => {
  const clips = getState().storyAuthoring.clips;
  const clipModal = getState().storyAuthoring.clipModal;
  const { story } = getState().storyAuthoring;
  const isAudioFile = story.custom_archive_media_type && story.custom_archive_media_type.startsWith("audio");

  try {
    const { data } = await axios.get<Clip>(`/stories/${storyId}/read_clip/${clipId}`);

    if (attempt < 10) {
      if (!data.getThumbnailURL || !data.getArchiveAudioURL || !data.getArchiveURL) {
        setTimeout(() => {
          dispatch(getClip({ storyId, clipId, attempt: attempt + 1 }));
        }, 5000);
      }
    }

    if (data.getThumbnailURL) {
      const updatedClip = { ...data, thumbnailURL: data.getThumbnailURL };
      const updatedClips = clips.map((clip) => (clip._id === clipId ? updatedClip : clip));
      dispatch(UpdateStoryClips({ clips: updatedClips }));
      if (clipModal.isOpen && clipModal._id === clipId) {
        dispatch(
          updateModalEditClip({
            clipId: clipId,
            clipDesc: data.description,
            clipTranscript: data.transcript,
            clipTitle: data.title,
            clipKeywords: data.tags,
            clipStart: Number(data.start),
            clipEnd: Number(data.end),
            clipDuration: story.duration,
            clipFragmentUrl: isAudioFile ? data.getArchiveAudioURL : data.getArchiveURL,
            clipExtendedFragmentUrl: data.getArchiveExtendedURL,
            isAudioFile: isAudioFile,
            videoUrl: data.getArchiveURL,
          })
        );
      }
    }
  } catch (error) {
    console.log(error);
  }
});

export const alignTranscript = (slate: SlateDocument) =>
  slate.map((block, idx: number, arr) =>
    fixParagraph({
      paragraph: block,
      nextParagraph: arr[idx + 1],
    })
  );

export const saveTranscript = createAsyncThunk<{ transcript?: { updated_at?: string } }, string, { state: RootState }>(
  "transcript/save",
  async (storyId: string, { getState }) => {
    const transcriptSlate = getState().storyAuthoring.transcriptSlate;
    if (!transcriptSlate) {
      console.warn("Attempted to save nonexistent transcript");
    } else {
      const alignedSlate = alignTranscript(transcriptSlate).filter((p) => p.children.some((c) => c.words.length > 0));

      const alignedDpe = convertSlateToDpe(sanitizeSlateForDpe(alignedSlate)) as DpeDocument;

      if (!checkDpeTranscriptDoc(alignedDpe)) {
        Sentry.withScope((scope) => {
          scope.setTag("storyId", storyId);
          scope.setExtra("transcriptSlate", transcriptSlate);
          scope.setTag("method", "saveTranscript");
          Sentry.captureMessage("Saving transcript with validation failures");
        });
      }

      const response = await axios.post(`/transcripts/${storyId}/edit`, alignedDpe);
      return response.data;
    }
    return null;
  }
);

const saveIndexes = createAsyncThunk<Array<{ updated_at: string }>, { storyId: string }, { state: RootState }>(
  "indexes/save",
  async ({ storyId }, { getState }) => {
    const idxs = getState().storyAuthoring.items;

    return (await Promise.all(idxs.map((idx) => axios.post(`/stories/${storyId}/index/${idx._id}`, idx)))).map(
      (resp) => resp.data
    );
  }
);

export const SAVE_STORY_LOADING_INDICATOR = "SAVE_STORY";

export const saveStoryAuthoring = createAsyncThunk<
  void,
  { storyId: string },
  { state: RootState; dispatch: AppDispatch }
>("story_authoring/save", async ({ storyId }, { dispatch }) => {
  // Oh sweet, sweet concurrency

  const [transcriptResp, indexesResp] = await Promise.all([
    dispatch(saveTranscript(storyId)).then(betterUnwrapResult),
    dispatch(saveIndexes({ storyId })).then(betterUnwrapResult),
  ]);
  dispatch(SetHasUnsavedChanges(false));

  // In case we don't have a transcript, don't fail the save
  if (transcriptResp?.transcript?.updated_at) {
    dispatch(SetLastSaved(new Date(transcriptResp.transcript.updated_at)));
  } else {
    const idx = indexesResp.find((idx_) => idx_.updated_at);
    dispatch(SetLastSaved(new Date(idx.updated_at)));
  }
});

export const updatePlayerProgress = ({ progressSeconds }) =>
  PlaybackProgress({
    progressSeconds,
  });

// TODO(jshearer): Keep track of last alignment, and flag as dirty on edit.

export interface ExportTranscriptParams {
  type: string;
  ext: string;
  speakers?: boolean;
  timecodes?: boolean;
  inlineTimecodes?: boolean;
  hideTitle?: boolean;
  atlasFormat?: boolean;
  isDownload?: boolean;
  downloadFunc?: (any, string) => void;
}

export const exportTranscript =
  ({
    type,
    ext,
    speakers,
    timecodes,
    inlineTimecodes,
    hideTitle,
    atlasFormat,
    isDownload,
    downloadFunc,
  }: ExportTranscriptParams) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    let slateData = getState().storyAuthoring.transcriptSlate;

    if (
      inlineTimecodes ||
      timecodes ||
      type === "json-slate" ||
      type === "json-digitalpaperedit" ||
      isCaptionType(type)
    ) {
      slateData = sanitizeSlateForDpe(
        alignTranscript(slateData).filter((p) => p.children.some((c) => c.words.length > 0))
      );
    }

    if (inlineTimecodes) {
      slateData = insertTimecodesInLineInSlateJs(slateData);
    }

    const transcriptTitle: string = getState().storyAuthoring.story.title;

    let editorContent =
      type === "json-slate-raw"
        ? slateData
        : exportAdapter({
            slateValue: slateData,
            type,
            transcriptTitle,
            speakers,
            timecodes,
            inlineTimecodes,
            hideTitle,
            atlasFormat,
          });

    if (ext === "json") {
      editorContent = JSON.stringify(editorContent, null, 2);
    }

    if (ext !== "docx" && isDownload) {
      (downloadFunc || download)(
        editorContent,
        `${
          transcriptTitle
            ?.toLowerCase()
            .replace(/\s\s/g, " ")
            .replace(/[^0-9a-zA-Z ]/g, "_") || "untitled"
        }.${ext}`
      );
    }

    return editorContent;
  };

export const getIndexAndParagraphs = () => (dispatch, getState: () => RootState) => {
  const {
    storyAuthoring: { transcriptSlate: transcriptionData, items: indexesData },
  } = getState();
  const transformIndexes = transformIndexFormat(indexesData);
  const indexesBuilded: IndexInterface[] = sortIndexesNewestFirst(buildIndexes(transcriptionData, transformIndexes));
  dispatch(
    SetHubPageParagraphs({
      paragraphs: transcriptionData,
      indexes: indexesBuilded,
    })
  );
};

export const searchInParagraph =
  ({ searchValue }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      storyAuthoring: {
        transcriptSlate: transcriptionData,
        hubPage: { paragraphs },
      },
    } = getState();

    let currPar = paragraphs;
    let newCount = 0;

    // if (searchValue) {
    const { newPar, count } = getHighlightedParagraphs(transcriptionData, searchValue, newCount);
    currPar = newPar;
    newCount = count;
    // }

    dispatch(
      SetParagraphSearchValue({
        paragraphs: currPar,
        search: {
          searchValue,
          totalFound: newCount,
          currentWordIndex: 0,
        },
      })
    );
  };

export const searchInChapters =
  ({ searchValue, chapters }) =>
  (dispatch: AppDispatch) => {
    let currChapters = chapters;
    let newCount = 0;
    if (searchValue) {
      currChapters = currChapters.map((chapter) => {
        const { newPar, count, title, description, keywords } = getHighlightedChapters(chapter, searchValue, newCount);
        newCount = count;
        return { ...chapter, paragraphs: newPar, title: title, description: description, keywords: keywords };
      });
    }

    dispatch(
      SetChaptersSearchValue({
        search: {
          searchValue,
          totalFound: newCount,
          currentChapters: currChapters,
          currentWordIndex: 0,
          semanticChunkTimes: [],
          semanticChunkEndTimes: [],
          semanticChunkScores: [],
        },
      })
    );
  };

export const searchInChaptersSemanticV2 =
  ({ storyId, searchValue }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!searchValue || !storyId) {
        return;
      }

      console.log(`[searchInChaptersSemanticV2] Searching for "${searchValue}" in story ${storyId}`);

      const response = await axios.post(`/knowledge-service/${storyId}/search`, {
        query: searchValue,
        mode: "hybrid",
        alpha: 0.5,
      });

      const { items: chapterResults } = response.data;

      console.log(`[searchInChaptersSemanticV2] Received ${chapterResults?.length || 0} chapter results`);

      // Get original chapters from state to preserve structure
      const state = getState();
      const { indexes, selectedIndexId } = state.storyAuthoring.hubPage;
      const index = indexes?.find((indexOption) => indexOption._id === selectedIndexId) ?? indexes?.[0];
      const originalChapters = index?.chapters || [];

      if (!chapterResults || chapterResults.length === 0) {
        console.log(`[searchInChaptersSemanticV2] No results found. Story may not be indexed yet.`);
        // Return original chapters unchanged
        dispatch(
          SetChaptersSearchValue({
            search: {
              searchValue,
              totalFound: 0,
              currentChapters: originalChapters,
              currentWordIndex: 0,
              semanticChunkTimes: [],
              semanticChunkEndTimes: [],
              semanticChunkScores: [],
            },
          })
        );
        return;
      }

      // Flatten all chunks from all chapter results for navigation only
      const allChunks: Array<{
        start_time: number;
        end_time: number;
        score: number;
      }> = [];
      chapterResults.forEach((chapterResult) => {
        if (chapterResult.chunks && Array.isArray(chapterResult.chunks)) {
          chapterResult.chunks.forEach((chunk) => {
            allChunks.push({
              start_time: chunk.start_time,
              end_time: chunk.end_time,
              score: chunk.score || 0,
            });
          });
        }
      });

      // Sort chunks by score (descending) - most relevant first
      allChunks.sort((a, b) => b.score - a.score);

      // Return original chapters completely unchanged - chunks are only used for navigation
      const transformedChapters = originalChapters.map((chapter) => ({
        ...chapter,
      }));

      const totalFound = allChunks.length;

      // Store chunk data (times, end times, and scores) for V2 navigation
      const chunkTimes = allChunks.map((chunk) => chunk.start_time);
      const chunkEndTimes = allChunks.map((chunk) => chunk.end_time);
      const chunkScores = allChunks.map((chunk) => chunk.score);

      console.log(
        `[searchInChaptersSemanticV2] Processed ${transformedChapters.length} chapters, ${totalFound} total chunks`
      );

      dispatch(
        SetChaptersSearchValue({
          search: {
            searchValue,
            totalFound,
            currentChapters: transformedChapters,
            currentWordIndex: 0,
            // Store chunk times, end times, and scores for V2 navigation
            semanticChunkTimes: chunkTimes,
            semanticChunkEndTimes: chunkEndTimes,
            semanticChunkScores: chunkScores,
          },
        })
      );
    } catch (error) {
      console.error("Failed to perform semantic search:", error);
      // On error, get original chapters and return them unchanged
      const state = getState();
      const { indexes, selectedIndexId } = state.storyAuthoring.hubPage;
      const index = indexes?.find((indexOption) => indexOption._id === selectedIndexId) ?? indexes?.[0];
      const originalChapters = index?.chapters || [];

      dispatch(
        SetChaptersSearchValue({
          search: {
            searchValue,
            totalFound: 0,
            currentChapters: originalChapters,
            currentWordIndex: 0,
            semanticChunkTimes: [],
            semanticChunkEndTimes: [],
            semanticChunkScores: [],
          },
        })
      );
    }
  };

export const setPrevWordIndex = () => (dispatch, getState: () => RootState) => {
  const {
    storyAuthoring: {
      hubPage: {
        search: { currentWordIndex, totalFound },
      },
    },
  } = getState();

  let newIndex;

  if (currentWordIndex > 0) {
    newIndex = currentWordIndex - 1;
  }
  if (currentWordIndex === 0) {
    newIndex = totalFound - 1;
  }

  dispatch(
    SetNewWordIndex({
      currentWordIndex: newIndex,
    })
  );
};

export const setTagSearch =
  ({ tagSearch }) =>
  (dispatch) => {
    dispatch(
      SetTagSearchValue({
        search: { tagSearch },
      })
    );
  };

export const setNextWordIndex = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    storyAuthoring: {
      hubPage: {
        search: { currentWordIndex, totalFound },
      },
    },
  } = getState();

  let newIndex;

  if (currentWordIndex < totalFound - 1) {
    newIndex = currentWordIndex + 1;
  }
  if (currentWordIndex === totalFound - 1) {
    newIndex = 0;
  }

  dispatch(
    SetNewWordIndex({
      currentWordIndex: newIndex,
    })
  );
};

export const setShowIndexes = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(SetShowIndexes({ value: value }));
};

export const setShowOnlySummaries = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(SetShowOnlySummaries(value));
};

export const setSelectedIndex = (indexId: string) => (dispatch: AppDispatch) => {
  dispatch(SetIndexId({ indexId }));
};

export const resetStoryAuthoring = () => (dispatch: AppDispatch) => {
  dispatch(ClearStoryAuthoringState());
};

export const setAutoScroll = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const isAutoScroll = getState().storyAuthoring.hubPage.isAutoScroll;
  dispatch(SetAutoScroll({ value: !isAutoScroll }));
};

export const setHighlightWords = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const isHighlightWords = getState().storyAuthoring.hubPage.isHighlightWords;
  dispatch(SetHighlightWords({ value: !isHighlightWords }));
};

export const setHasHighlightedStartToEndWords = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(SetHasHighlightedStartToEndWords({ value }));
};

export const setIsSearchActive = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(SetIsSearchActive({ value }));
};

export const generatePublicPageURL = (storyId: string) => async (dispatch) => {
  try {
    await axios.get<string>(`/stories/${storyId}/share/public`).then((response) => response.data);
  } catch (err) {
    dispatch(notifyError("Failed to generate public page URL"));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setTag("method", "generatePublicPageURL");
      Sentry.captureMessage("Failed to generate public page URL");
      Sentry.captureException(err);
    });
  }
};

export const disablePublicPageURL = (storyId: string) => async (dispatch) => {
  try {
    await axios.get<string>(`/stories/${storyId}/unshare/public`).then((response) => response);
  } catch (err) {
    dispatch(notifyError("Failed to disable public page URL"));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setTag("method", "disablePublicPageURL");
      Sentry.captureMessage("Failed to disable public page URL");
      Sentry.captureException(err);
    });
  }
};

export const fetchTranscriptOnly = createAsyncThunk("transcript/fetchOnly", async (storyId: string, { dispatch }) => {
  try {
    // 1. Check asyncOperations for transcribe status
    const storyResponse = await axios.get<Story>(`/stories/${storyId}`);
    const dailyPreviewState = await fetchDailyPreviewState(storyResponse.data);
    dispatch(
      StoryPlaybackStateRefreshed({
        _id: storyResponse.data._id,
        archiveURL: storyResponse.data.archiveURL,
        thumbnailURL: storyResponse.data.thumbnailURL,
        call_processing_status: storyResponse.data.call_processing_status,
        asyncOperations: storyResponse.data.asyncOperations || [],
        isDailyPreviewPending: storyResponse.data.isDailyPreviewPending === true,
        ...dailyPreviewState,
      })
    );
    const asyncOps = storyResponse.data.asyncOperations || [];
    const transcribeOp = asyncOps.find((op) => op.type === "transcribe");
    console.log("[fetchTranscriptOnly] transcribeOp:", transcribeOp);

    if (transcribeOp && transcribeOp.status === "completed") {
      // 2. Fetch transcript content
      const transcriptResponse = await axios.get(`/transcripts/${storyId}`);
      const transcript = transcriptResponse.data?.transcript;
      if (transcript) {
        const transcriptionData = convertDpeToSlate(transcript);
        // Update transcriptSlate (stops polling)
        dispatch(TranscriptUpdated({ transcriptSlate: transcriptionData }));
        // Build and update indexes
        const indexesData = transcriptResponse.data.story.indexes;
        const transformIndexes = transformIndexFormat(indexesData);
        const indexesBuilded = sortIndexesNewestFirst(buildIndexes(transcriptionData, transformIndexes));
        dispatch(
          SetHubPageParagraphs({
            paragraphs: transcriptionData,
            indexes: indexesBuilded,
          })
        );
        // update hubPage.loadedTranscript to true
        dispatch(SetTranscriptLoadingState({ loadingTranscript: false, loadedTranscript: true }));
        // use notification api to show a message that the transcript is updated
        dispatch(notifySuccess("Transcription Completed!"));
        // send push notification
        sendPushNotification({
          title: "Transcription Completed!",
          body: "The transcription is now ready for you to review.",
          url: window.location.href, // or a specific URL if you want
        });
      }
    }
  } catch (e) {
    console.error("Failed to fetch transcript for polling:", e);
  }
});

// TEMPORARY: Self-healing repair for corrupted WebM testimonial durations (> 7,000,000s).
// Can be safely removed once all affected legacy testimonial stories are repaired.
export const repairTestimonialDuration = (storyId: string, duration: number) => async (dispatch: AppDispatch) => {
  try {
    await axios.patch(`/stories/${storyId}/repair-duration`, { duration });
    dispatch(SetStoryDuration({ duration }));
  } catch (err) {
    console.error("[repairTestimonialDuration] Failed to repair duration:", err);
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setTag("method", "repairTestimonialDuration");
      Sentry.captureException(err);
    });
  }
};
